import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PageContainer from '../../../../components/Container/pageContainer';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { ShowLoader } from '../../../../redux/actions/loader';
import PrintIcon from '@mui/icons-material/Print';

const LoanSuppliers = () => {
  // State for main data
  const [loanSuppliers, setLoanSuppliers] = useState([]);
  const [cashBalance, setCashBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const api = useSelector((state) => state.Api);
  const dispatch = useDispatch()
  // State for payment dialog
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [totalRemaining, setTotalRemaining] = useState(0);
  const initSup = useRef(false);
  const initRoz= useRef(false);
  
  // Pashto translations
  const translations = {
    title: "پدې برخه کې تاسو کولی سئ ټول هغه بیلونه وګورئ چې د عرضه کوونکي سره جوړ سوي دي او تاسو کولی سئ عرضه کوونکي ته هم دپور پیسې ورکړئ که تاسو پور لرئ.",
    titleHead: "دټولو عرضه کوونکو ټوټل پور",
    availableCash: "دټولو عرضه کوونکو ټوټل پور",
    supplierId: "پور ورکوونکی نمبر",
    supplierName: "نوم",
    phoneNumber: "تلیفون نمبر",
    totalLoan: "ټول پور",
    totalPaid: "ورکړ سوي پیسې",
    amountRemaining: "پاتې دپور پیسې",
    action: "عمل",
    pay: "ورکړه",
    makePayment: "پیسې ورکړه",
    paymentAmount: "د پیسو اندازه",
    cancel: "لغوه",
    dailyCash: "دخل ",
    confirmPayment: "تایید ",
    paymentHistory: "د پیسو تفصیل",
    description: "تفصیل",
    date: "نیټه",
    payIn: "مبلغ بهر",
    payOut: "مبلغ دننه",
    noRecords: "ریکارډ نشته",
    loading: "بار کول...",
    error: "تېروتنه",
    totalBill: "ټوټل بل",
    balance: "بیلینس",
  };

  useEffect(() => {
    fetchLoanSuppliers();
    fetchRoznamcha();
  }, []);

  const fetchLoanSuppliers = async () => {
    if(initSup.current) return
    initSup.current = true
    dispatch(ShowLoader('1'))
    const userToken = JSON.parse(localStorage.getItem("User_Data"))?.token || undefined;
    try {
      const response = await axios.get(api.showSupplierDefault, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
      });
      if (response.status === 200) {
        // Initialize suppliers without payment history (will be fetched later when expanded)
        const suppliersWithOpen = response.data.data.map(supplier => ({
          ...supplier,
          open: false,
          paymentHistory: null, // Changed from [] to null to indicate not loaded yet
          historyLoading: false,
          historyError: null
        }));
        const totalRemaining = response.data.data.reduce((sum, supplier) => sum + supplier.amount_remaining, 0);
        setTotalRemaining(totalRemaining)
//console.log("Total Amount Remaining:", totalRemaining);

        setLoanSuppliers(suppliersWithOpen);
        setLoading(false);
        dispatch(ShowLoader('0'))
      }
    } catch (err) {
      setError('Failed to fetch loan suppliers');
      setLoading(false);
      console.error(err);
    }
  };

  const fetchRoznamcha = async () => {
    if(initRoz.current) return
    initRoz.current = true
    
    const userToken = JSON.parse(localStorage.getItem("User_Data"))?.token || undefined;
    try {
      const response = await axios.post(api.showRoznamcha, {}, { // Added empty body object
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      });
      if (response.status === 200) {
        const roz = response.data;
        const todayIn = response?.data?.today_payments?.reduce((sum, row) => sum + row?.amount_in, 0);
        const todayOut = response?.data?.today_payments?.reduce((sum, row) => sum + row?.amount_out, 0);               
        const balance = todayIn - todayOut + roz?.till_yesterday?.balance;
        setCashBalance(balance);     
      }
    } catch (err) {
      console.error("Error fetching roznamcha:", err);
    }
  };

  const fetchPaymentHistory = async (supplierId, index) => {
    dispatch(ShowLoader('1'))
    const userToken = JSON.parse(localStorage.getItem("User_Data"))?.token || undefined;
    
    // Update loading state for this supplier
    const updatedSuppliers = [...loanSuppliers];
    updatedSuppliers[index].historyLoading = true;
    updatedSuppliers[index].historyError = null;
    setLoanSuppliers(updatedSuppliers);

    try {
      const response = await axios.get(`${api.supplierPayments}?supplier=${supplierId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      });
      
      if (response.status === 200) {
        //console.log(response.data)
        const updatedSuppliersWithHistory = [...loanSuppliers];
        updatedSuppliersWithHistory[index].paymentHistory = response.data;
        updatedSuppliersWithHistory[index].historyLoading = false;
        setLoanSuppliers(updatedSuppliersWithHistory);
        dispatch(ShowLoader('0'))
      }
    } catch (err) {
      console.error("Error fetching payment history:", err);
      const updatedSuppliers = [...loanSuppliers];
      updatedSuppliers[index].historyLoading = false;
      updatedSuppliers[index].historyError = translations.error;
      setLoanSuppliers(updatedSuppliers);
    }
  };

  const toggleRow = async (index) => {
    const supplier = loanSuppliers[index];
    const updatedSuppliers = [...loanSuppliers];
    //console.log(loanSuppliers)
    
    // Toggle the open state
    updatedSuppliers[index].open = !supplier.open;
    setLoanSuppliers(updatedSuppliers);

    // Fetch payment history if not already loaded
    if (updatedSuppliers[index].open && updatedSuppliers[index].paymentHistory === null) {
      await fetchPaymentHistory(supplier.supplier_id, index);
    }
  };

  const handlePaymentClick = (supplier) => {
    setSelectedSupplier(supplier);
    setPaymentAmount('');
    setOpenPaymentDialog(true);
  };

  const handlePaymentSubmit = async () => {
    try {
//console.log(selectedSupplier)
        
        //console.log(paymentAmount, selectedSupplier?.supplier_id)
        // return false
        const userToken = JSON.parse(localStorage.getItem("User_Data"))?.token || undefined;
        const payload = {supplierId:selectedSupplier?.supplier_id, amount:paymentAmount, supplier_name: selectedSupplier.supplier_name}
        //console.log(payload)
        // return false
        try {
            const response = await axios.post(api.payBySupplier, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userToken}`,
                },
            });
            if (response.status === 200) {
                //console.log(response.data) 
                setSelectedSupplier({ ...selectedSupplier });
                initSup.current = false
                initRoz.current = false
                // fetchRoznamcha()
                // setRoznamcha(response.data);
            }
        } catch (err) {
            console.error("Error fetching suppliers:", err);
        }
      
      // Refresh data
      await fetchLoanSuppliers();
      await fetchRoznamcha();
      
      setOpenPaymentDialog(false);
    } catch (err) {
      console.error('Payment failed', err);
      alert('Payment failed. Please try again.');
    }
  };

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) > 0 && Number(value) <= selectedSupplier?.amount_remaining)) {
      setPaymentAmount(value);
    }
  };

  
  const handlePrintBill = async (rows) => { 
  try {
    if (!rows?.paymentHistory || rows.paymentHistory.length === 0)
      return alert("No rows to print.");

    const storeInfo = {
      logo: localStorage.getItem("businessLogo") || "",
      businessName: localStorage.getItem("businessName"),
    };

    const printWindow = window.open("", "_blank");

     let runningBalance = 0;
      const data = rows?.paymentHistory?.map((bill, i) => {
        const inAmt = Number(bill.pay_in) || 0;
        const outAmt = Number(bill.pay_out) || 0;
        runningBalance +=   inAmt - outAmt;
        const desc = `${bill?.pay_out?.toLocaleString()} نغدی | ${bill?.pay_in?.toLocaleString()} پور | ${bill?.pay_in?.toLocaleString()} ټوټل بل | ${bill?.description}`;
        const description = bill?.pay_type == 'CASH' ? `${bill?.description} ${bill?.pay_in?.toLocaleString()} ټوټل بل` : bill?.pay_type == 'LOAN' ?  desc : bill?.description        
        return [
          runningBalance.toFixed(2),
          outAmt.toFixed(2),
          inAmt.toFixed(2),
          description,
          new Date(bill.pay_date).toLocaleDateString(), 
        ];
      });
    
      const totalBalance = rows.paymentHistory.reduce(
        (sum, item) => sum + (item.pay_in || 0) - (item.pay_out || 0),
        0
      ).toLocaleString(); 

    const footerRemarks = `<tr><td colspan=4>ټوټل بیلینس	 </td> <td> ${totalBalance}</td></tr>`
 
    const newTableRows = data
      .map(
        (row) => `
          <tr>
            <td>${row[4] || ""}</td> 
            <td>${row[3] || ""}</td>
            <td>${row[2] || ""}</td>
            <td>${row[1] || ""}</td>
            <td>${row[0] || ""}</td>
          </tr>`
      )
      .join("");

    const logoPath = storeInfo.logo
      ? storeInfo.logo
      : "/images/logos/Logo.png";

    const styles = `
      <style>
        @page {
          size: A4;
          margin: 15mm;
        }
        body {
          font-family: 'Noto Naskh Arabic', sans-serif;
          margin: 0;
          background-color: white;
           background-repeat: no-repeat;
          background-position: center 50%;
          background-size: 60%;
          -webkit-print-color-adjust: exact !important;
        }
          .page {
          width: 210mm;
          min-height: 297mm;
          padding: 10px;          /* ✅ 10px left & right */
          box-sizing: border-box;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 4px;
          text-align: center;
        }
        thead {
          display: table-header-group; 
          color: white;
        }
        tbody tr:nth-child(odd) {
          background-color: #f9f9f9;
        }
        .header-info {
          text-align: center;
          font-size: 13px;
          padding: 4px 0;
        }
        .logo {
          max-width: 60px;
        }
        footer {
          text-align: center;
          font-size: 11px;
          margin-top: 10px;
        }
      </style>
    `;

    const printHTML = `
      <htmllang="ps">
        <head>
          <meta charset="UTF-8" />
          <title>بیل ${rows?.first_name || ""}</title>
          ${styles}
        </head>
        <body>
  <div class="page">
    <table>
              <thead>
                <tr style="color: black">
                  <td colspan="6" class="header-info" style="border:0px">
                    <div style="display:flex; justify-content:space-between; align-items:center;">
                      <div style="float: right; text-align: right;">
                        <h3 style="margin: 0; padding:0px; font-size: 12px">${rows?.supplier_name || ""}</h3>
                        <h3 style="margin: 0; padding:0px; font-size: 12px">${rows?.phone_number || ""}</h3>
                        <h3 style="margin: 0; padding:0px; font-size: 12px">${rows?.address || ""}</h3>
                      </div>
                      <div style="text-align:left;">
                        <img src="${logoPath}" class="logo" />
                        <h2 style="font-size:12px; margin:0;">${storeInfo.businessName || ""}</h2>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr style="background-color: #ffa800">
                  <th>نیټه</th> 
                  <th>د پرداخت ډول</th>
                  <th>مبلغ دننه</th>
                  <th>مبلغ بهر</th>
                  <th>بیلانس</th>
                </tr>
              </thead>
              <tbody>
                ${newTableRows}
              </tbody>
            </table>
          </div>

          <footer>© ${new Date().getFullYear()} ${ "واصلي ټیکنالوژي 0703131865"}</footer>

          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printHTML);
    printWindow.document.close();
  } catch (err) {
    console.error("Error printing bill:", err);
  }
};
  return (
    <PageContainer title={translations.titleHead} description="پور ورکوونکي">
      <Grid container spacing={3} direction="rtl">
        <Grid item xs={12}>
          <Typography variant="h4" spacing={5} textAlign={'center'} gutterBottom>
            {translations.titleHead}
          </Typography>
          <Typography variant="h3" textAlign={'center'} spacing={2} style={{fontWeight:'bold', color:'red'}} color="textSecondary" gutterBottom>
            {translations.availableCash} {totalRemaining.toLocaleString()}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{translations.supplierId}</TableCell>
                    <TableCell>{translations.supplierName}</TableCell>
                    <TableCell>{translations.phoneNumber}</TableCell>
                    <TableCell align="right">{translations.totalLoan}</TableCell>
                    <TableCell align="right">{translations.totalPaid}</TableCell>
                    <TableCell align="right">{translations.amountRemaining}</TableCell>
                    <TableCell>{translations.action}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {translations.loading}
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" color="error">
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : loanSuppliers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {translations.noRecords}
                      </TableCell>
                    </TableRow>
                  ) : (
                    loanSuppliers.map((supplier, index) => (
                      <React.Fragment key={supplier.supplier_id}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => toggleRow(index)}
                            >
                              {supplier.open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{supplier.supplier_id}</TableCell>
                          <TableCell>{supplier.supplier_name}</TableCell>
                          <TableCell>{supplier.phone_number}</TableCell>
                          <TableCell align="right">{supplier.total_billed_amount?.toLocaleString()}</TableCell>
                          {/* <TableCell align="right">{(supplier.total_amount - supplier.loan_amount).toLocaleString()}</TableCell> */}
                          <TableCell align="right">{supplier.total_paid?.toLocaleString()}</TableCell>
                          <TableCell align="right">{supplier.amount_remaining?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={true}
                              // onClick={() => handlePaymentClick(supplier)}
                            >
                              {translations.pay}
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={supplier.open} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="h6" gutterBottom component="div">
                                  {translations.paymentHistory}
                                  <IconButton aria-label="print" 
                                        // onClick={() => handlePrint(supplier)}>
                                        onClick={() => handlePrintBill(supplier)}>
                                  <PrintIcon />
                                </IconButton>
                                </Typography>
                                {supplier.historyLoading ? (
                                  <Typography>{translations.loading}</Typography>
                                ) : supplier.historyError ? (
                                  <Typography color="error">{supplier.historyError}</Typography>
                                ) : supplier.paymentHistory === null ? (
                                  <Typography>{translations.noRecords}</Typography>
                                ) : supplier.paymentHistory.length === 0 ? (
                                  <Typography>{translations.noRecords}</Typography>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>{translations.date}</TableCell>
                                        <TableCell align="left">{translations.description}</TableCell>
                                        {/* <TableCell align="right">{translations.totalBill}</TableCell> */}
                                        <TableCell align="right">{translations.payOut}</TableCell>
                                        <TableCell align="right">{translations.payIn}</TableCell>
                                        <TableCell align="right">{translations.balance}</TableCell>
                                      </TableRow>
                                    </TableHead>
<TableBody>
  {supplier.paymentHistory.map((history, idx, array) => {
    // Calculate running balance
    const runningBalance = array
      .slice(0, idx + 1)
      .reduce((sum, item) => sum + (item.pay_in || 0) - (item.pay_out || 0), 0);
    
    return (
      <TableRow key={idx}>
        <TableCell>{history.pay_date}</TableCell>
        <TableCell align="left">
          {history.pay_type === 'CASH' ?  
            <table>
              <tr>
                <td>{`${history?.pay_in?.toLocaleString()} ټوټل بل`}</td>
                <td>{history?.description}</td>
              </tr>
            </table>
            : history.pay_type === 'LOAN' ? 
            <table>
              <tr>
                <td>{(history?.pay_out)?.toLocaleString()} نغدی</td>
                <td>{(history?.pay_in)?.toLocaleString()} پور</td>
                <td>{` ${history?.pay_in?.toLocaleString()} ټوټل بل`}</td>
                <td>{history?.description}</td>
              </tr>
            </table>
            : history.description
          }
        </TableCell>
        <TableCell align="right">{history.pay_in?.toLocaleString()}</TableCell>
        <TableCell align="right">{history.pay_out?.toLocaleString()}</TableCell>
        <TableCell align="right">{runningBalance.toLocaleString()}</TableCell>
      </TableRow>
    );
  })}
  {/* Total row */}
  <TableRow>
    <TableCell align="right">
      {/* {supplier.paymentHistory.reduce((sum, item) => sum + (item.pay_in || 0), 0).toLocaleString()} */}
    </TableCell>
    <TableCell align="right">
      {/* {supplier.paymentHistory.reduce((sum, item) => sum + (item.pay_out || 0), 0).toLocaleString()} */}
    </TableCell>
    <TableCell align="right" colSpan={2}>ټوټل بیلینس</TableCell>
    <TableCell align="right">
      {supplier.paymentHistory.reduce((sum, item) => sum + (item.pay_in || 0) - (item.pay_out || 0), 0).toLocaleString()}
    </TableCell>
  </TableRow>
</TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} minWidth='md' fullWidth maxWidth='xs' onClose={() => setOpenPaymentDialog(false)}>
        <DialogTitle style={{ textAlign: 'right' }}>{translations.makePayment}</DialogTitle>
        <DialogContent style={{ textAlign: 'right' }}>
          {selectedSupplier && (
            <Box>
              <Typography variant="subtitle1">
                 {selectedSupplier.supplier_name}
              </Typography>
              <Typography variant="subtitle1">
                {translations.amountRemaining}: {selectedSupplier.amount_remaining?.toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" gutterBottom style={cashBalance > 0 ? {color: 'green'} : {color: 'red'}}>
                <h2>{translations.dailyCash}: {cashBalance.toLocaleString()}</h2>
              </Typography>
              
              <TextField
                autoFocus
                margin="dense"
                label={translations.paymentAmount}
                type="number"
                fullWidth
                variant="standard"
                value={paymentAmount}
                onChange={handlePaymentAmountChange}
                inputProps={{
                  min: 0,
                  max: Math.min(selectedSupplier.amount_remaining, cashBalance)
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'flex-start' }}>
          <Button 
            onClick={handlePaymentSubmit}
            disabled={!paymentAmount || Number(paymentAmount) <= 0}
          >
            {translations.confirmPayment}
          </Button>
          <Button onClick={() => setOpenPaymentDialog(false)}>{translations.cancel}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default LoanSuppliers;